import { motion } from "framer-motion";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Target, BarChart, Brain, Heart } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const MediaStrategy = () => {
  const {colors, theme} = useTheme();
  return (
    <>
      <Navigation />
      <div
        className="pt-16 min-h-screen transition-colors duration-300"
        style={{ backgroundColor: colors.background, color: colors.foreground }}
      >
        <section className="py-16 md:py-20 px-4 relative overflow-hidden">
          {/* Animated background */}
          <div className="absolute inset-0 -z-10">
            <motion.div
              className="absolute top-20 left-20 w-96 h-96 rounded-full blur-3xl"
              style={{ backgroundColor: `${colors.primary}10` }}
              animate={{
                scale: [1, 1.2, 1],
                x: [0, 50, 0],
              }}
              transition={{
                duration: 18,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <motion.div
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}20)`,
                  border: `3px solid ${colors.primary}30`,
                  boxShadow: `0 8px 30px ${colors.primary}20`
                }}
                whileHover={{ 
                  scale: 1.1,
                  rotate: 360,
                  transition: { duration: 0.6 }
                }}
              >
                <Target className="w-10 h-10" style={{ color: colors.primary }} />
              </motion.div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span 
                  className="bg-clip-text text-transparent"
                  style={{ background: colors.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                >
                  Media Strategy
                </span>
              </h1>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="prose prose-lg max-w-none space-y-6"
              style={{ color: colors.secondary }}
            >
              <p className="text-lg md:text-xl leading-relaxed text-center">
                At Agape, we build emotional bridges between content and
                consumers. Our media strategies are data-informed and
                audience-tuned, designed to help brands resonate where it
                matters most. By harnessing cultural insight and behavioral
                trends, we craft campaigns that connect on a personal, emotional
                level.
              </p>

              <p className="text-lg md:text-xl leading-relaxed text-center">
                Our approach is part science, part soul-mixing analytics,
                intuition, and creativity to deliver performance-driven media
                plans that reflect the spirit of modern storytelling.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16"
            >
              {[
                {
                  icon: BarChart,
                  title: "Data-Informed",
                  desc: "Analytics-driven strategy development",
                },
                {
                  icon: Brain,
                  title: "Cultural Insight",
                  desc: "Understanding behavioral trends and patterns",
                },
                {
                  icon: Heart,
                  title: "Emotional Connection",
                  desc: "Personal, meaningful campaign messaging",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  className="group relative h-full"
                >
                  {/* Ultra-fast blur effects */}
                  <div 
                    className="absolute rounded-2xl opacity-18 group-hover:opacity-28 transition-all duration-150 ease-out"
                    style={{
                      background: `radial-gradient(circle at center, ${colors.primary}10, ${colors.secondary}06, transparent 65%)`,
                      filter: 'blur(45px)',
                      transform: 'scale(1.25)',
                      inset: '-15px',
                      boxShadow: `0 0 50px ${colors.primary}10, 0 0 80px ${colors.primary}06`
                    }}
                  />
                  
                  <div 
                    className="absolute rounded-2xl opacity-22 group-hover:opacity-35 transition-all duration-100 ease-out"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}08, ${colors.secondary}06)`,
                      filter: 'blur(25px)',
                      transform: 'scale(1.15)',
                      inset: '-10px',
                    }}
                  />
                  
                  <motion.div
                    whileHover={{ 
                      scale: 1.05,
                      y: -8,
                      transition: { duration: 0.3, ease: "easeOut" }
                    }}
                    className="relative p-8 rounded-xl text-center border-2
                             shadow-xl hover:shadow-2xl transition-all duration-300 h-full flex flex-col backdrop-blur-xl"
                    style={{ 
                      backgroundColor: `${colors.card}98`,
                      borderColor: `${colors.primary}85`,
                      boxShadow: `
                        0 15px 30px ${colors.primary}12, 
                        0 8px 20px ${colors.primary}08, 
                        0 4px 12px ${colors.primary}06,
                        inset 0 1px 2px ${colors.primary}20,
                        inset 0 -1px 2px ${colors.secondary}12,
                        0 0 0 1px ${colors.primary}25 inset
                      `,
                      backdropFilter: 'blur(20px) saturate(1.5)',
                    }}
                  >
                    {/* Fast inner glow */}
                    <div 
                      className="absolute inset-0 rounded-2xl opacity-15 group-hover:opacity-25 transition-opacity duration-150 ease-out"
                      style={{
                        background: `radial-gradient(circle at 30% 30%, ${colors.primary}18 0%, ${colors.secondary}10 30%, transparent 70%)`
                      }}
                    />
                    
                    {/* Fast acrylic overlay */}
                    <div 
                      className="absolute inset-2 rounded-xl opacity-25 group-hover:opacity-35 transition-opacity duration-100 ease-out"
                      style={{
                        background: `linear-gradient(135deg, ${colors.background}18, ${colors.card}10, ${colors.primary}05)`,
                        backdropFilter: 'blur(12px)',
                      }}
                    />
                    
                    {/* Floating icon container */}
                    <motion.div
                      className="relative z-10 mb-6"
                      whileHover={{ 
                        scale: 1.1,
                        rotate: 180,
                        transition: { duration: 0.4, ease: "easeOut" }
                      }}
                    >
                      <div
                        className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}18)`,
                          border: `2px solid ${colors.primary}35`,
                          boxShadow: `0 4px 14px ${colors.primary}15`
                        }}
                      >
                        <item.icon className="w-8 h-8" style={{ color: colors.primary }} />
                      </div>
                    </motion.div>
                    
                    <h3 
                      className="relative z-10 text-xl font-bold mb-3" 
                      style={{ color: colors.primary }}
                    >
                      {item.title}
                    </h3>
                    
                    <p 
                      className="relative z-10 flex-grow" 
                      style={{ color: colors.secondary }}
                    >
                      {item.desc}
                    </p>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>

      <style>{`
        @keyframes shimmer {
          0%, 100% { transform: translateX(-100%) rotate(45deg); }
          50% { transform: translateX(200%) rotate(45deg); }
        }
      `}</style>
    </>
  );
};

export default MediaStrategy;
